<template>
  <v-dialog
    v-model="show"
    scrollable
    max-width="840px"
    @click:outside="close()"
  >
    <v-card>
      <v-card-title class="px-6 pt-5 pb-4 align-start">
        <div class="overflow-hidden">
          <div class="text-h6 text-truncate">
            {{ name }}
          </div>
          <div class="caption grey--text mt-1">
            {{ loading ? "Loading users…" : `${users.length} users` }}
          </div>
        </div>
        <v-spacer />
        <v-btn
          id="migration_users_export"
          color="primary"
          class="elevation-0"
          :disabled="loading || !users.length"
          @click="download()"
        >
          <v-icon small>
            mdi-cloud-download
          </v-icon>
        </v-btn>
      </v-card-title>

      <div
        v-if="!loading && users.length"
        class="px-6 pb-4 d-flex flex-wrap"
      >
        <v-chip
          v-for="count in counts"
          :key="`count--${count.label}`"
          small
          label
          :color="count.color"
          text-color="white"
          class="mr-2 mb-1 count-chip"
        >
          {{ count.label }}: {{ count.value }}
        </v-chip>
      </div>

      <v-divider />

      <v-card-text class="pa-0">
        <v-skeleton-loader
          v-if="loading"
          type="table-thead, table-tbody"
          class="pa-4"
        />
        <v-data-table
          v-else
          :headers="headers"
          :items="users"
          :items-per-page="10"
          :footer-props="{ 'items-per-page-options': [10, 25, -1] }"
        >
          <template v-slot:item.name="{ item }">
            <span class="font-weight-medium">{{ item.name }}</span>
          </template>

          <template v-slot:item.emailID="{ item }">
            <span class="grey--text text--darken-2">
              {{ item.emailID || "—" }}
            </span>
          </template>

          <template v-slot:item.lastLogin="{ item }">
            <span class="grey--text text--darken-2">
              {{ formatLastLogin(item.lastLogin) }}
            </span>
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip
              small
              label
              :color="statusOf(item).color"
              text-color="white"
              class="status-chip"
            >
              {{ statusOf(item).label }}
            </v-chip>
          </template>

          <template v-slot:no-data>
            <div class="py-10 grey--text">
              No users found
            </div>
          </template>
        </v-data-table>
      </v-card-text>

      <v-divider />
      <v-card-actions class="px-6 py-3">
        <v-spacer />
        <v-btn
          id="migration_users_close"
          text
          @click.native="close()"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { failure, csvDownload } from "@/mixins/common";

// keyed so the tallies in counts() cannot drift from the labels they count
const STATUSES = {
  old: { label: "Old login", color: "error" },
  invited: { label: "Invited", color: "warning" },
  migrated: { label: "New login", color: "success" }
};

export default {
  name: "MigrationUsers",
  mixins: [failure, csvDownload],
  props: {
    showDialog: {
      type: Boolean,
      default: false
    },
    // "accounts" or "corporates": the admin API resource the users hang off
    scope: {
      type: String,
      required: true
    },
    entityId: {
      type: Number,
      required: true
    },
    name: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      users: [],
      loading: false,
      requestId: 0,
      headers: [
        { text: "User", value: "name" },
        { text: "Email", value: "emailID" },
        { text: "Last login", value: "lastLogin" },
        { text: "Login", value: "status", sortable: false, align: "end" }
      ]
    };
  },
  computed: {
    // needs a setter: Esc makes v-dialog write false back through v-model, and
    // without one the parent's flag stays true and the dialog never reopens
    show: {
      get() {
        return this.showDialog;
      },
      set(visible) {
        this.$emit("update:showDialog", visible);
      }
    },
    counts() {
      return Object.keys(STATUSES).map((key) => ({
        label: STATUSES[key].label,
        color: STATUSES[key].color,
        value: this.users.filter((user) => this.statusKey(user) === key).length
      }));
    }
  },
  watch: {
    // immediate: the parent mounts this component with showDialog already true,
    // so a change-only watcher never fires on the first open.
    show: {
      immediate: true,
      handler(visible) {
        if (visible) {
          this.getUsers();
        }
      }
    }
  },
  methods: {
    close() {
      this.$emit("update:showDialog", false);
    },
    getUsers() {
      // the instance outlives a close, so drop the previous entity's rows before
      // fetching and ignore a slow reply that a newer open has superseded
      const request = ++this.requestId;
      this.users = [];
      this.loading = true;
      this.$http.get(`${this.ADMIN_API_URL}/${this.scope}/${this.entityId}/users`).then(
        (res) => {
          if (request !== this.requestId) {
            return;
          }
          // the list endpoint has no active_status filter, but the summary counts
          // exclude deactivated users; filter here so the two agree
          this.users = (res.body || []).filter((user) => user.activeStatus !== false);
          this.loading = false;
        },
        (failure) => {
          if (request !== this.requestId) {
            return;
          }
          this.loading = false;
          this.handleFailure(failure);
        }
      );
    },
    statusKey(user) {
      if (!user.ssoEnabled) {
        return "old";
      }
      if (!user.ssoVerified) {
        return "invited";
      }
      return "migrated";
    },
    // null = never signed in since tracking began (not the same as "long ago")
    formatLastLogin(value) {
      if (!value) {
        return "—";
      }
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
    },
    statusOf(user) {
      return STATUSES[this.statusKey(user)];
    },
    download() {
      this.downloadCsv(
        `login_migration_${this.name}`,
        ["User", "Email", "Last login", "Login"],
        this.users.map((user) => [
          user.name,
          user.emailID,
          user.lastLogin ? this.formatLastLogin(user.lastLogin) : "",
          this.statusOf(user).label
        ])
      );
    }
  }
};
</script>

<style scoped>
/* fixed widths so the labels don't leave a ragged edge down the column */
.status-chip {
  min-width: 92px;
  justify-content: center;
}

.count-chip {
  min-width: 112px;
  justify-content: center;
}

/* the theme defines a colour named "active", which generates a global
   background class; v-data-table puts "active" on the sorted th, so the
   header would otherwise fill solid orange */
::v-deep .v-data-table th.active {
  background-color: transparent !important;
}
</style>
